import { VITE_ENV } from 'config/constants';

const DB_NAME = `protector-${VITE_ENV}`;
export const FIELDS_STORE_NAME = 'fields';
export const FIELDS_BY_SEASONS_STORE_NAME = 'fieldsBySeasons';
export const FIELDS_BY_SEASONS_SYNC_META_STORE_NAME = 'fieldsBySeasonsSyncMeta';
export const PROPERTY_ID_INDEX = 'property_id';
export const SYNC_META_PROPERTY_ID_INDEX = 'propertyId';

// Any changes on tables need a upgrade version
const DB_VERSION = 6;

interface IndexedDBIndex {
  name: string;
  unique: boolean;
}
interface IndexedDBStore {
  name: string;
  keyPath: string | string[];
  indexes: IndexedDBIndex[];
}

const ensureErrorAndReject = (reject: (reason?: unknown) => void) => (error: unknown) => {
  reject(error instanceof Error ? error : new Error(String(error)));
};

/**
 * These two stores must always be wiped together on a DB version upgrade: the sync-meta store's
 * `lastCalledAt` watermark is only valid alongside the field rows it was recorded for. Wiping one
 * without the other here would make the next `/sync` call trust a watermark for data that's no
 * longer cached locally.
 *
 * This invariant is specific to the upgrade path below — at runtime, `evictStaleFields` in
 * region.fields-cropzone-cache.ts prunes individual stale rows from `fieldsBySeasons` without
 * touching `fieldsBySeasonsSyncMeta`, which is fine because it only removes rows that are no
 * longer relevant, not the watermark for seasons still in use.
 */
const CACHE_ONLY_STORES_REPLACED_ON_UPGRADE = [FIELDS_BY_SEASONS_STORE_NAME, FIELDS_BY_SEASONS_SYNC_META_STORE_NAME];

const applyUpgrade = (db: IDBDatabase, stores: IndexedDBStore[]) => {
  CACHE_ONLY_STORES_REPLACED_ON_UPGRADE.forEach(storeName => {
    if (db.objectStoreNames.contains(storeName)) {
      db.deleteObjectStore(storeName);
    }
  });

  stores.forEach(store => {
    if (!db.objectStoreNames.contains(store.name)) {
      const createdStore = db.createObjectStore(store.name, { keyPath: store.keyPath });
      const indexes = store.indexes || [];
      indexes.forEach(index => {
        createdStore.createIndex(index.name, index.name, { unique: index.unique });
      });
    }
  });
};

let dbConnectionPromise: Promise<IDBDatabase> | null = null;

const openNewConnection = (stores: IndexedDBStore[], version: number) => {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, version);
    request.onupgradeneeded = (e: Event) => {
      applyUpgrade((e?.target as IDBOpenDBRequest).result, stores);
    };
    request.onsuccess = (event: Event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      db.onversionchange = () => {
        db.close();
        dbConnectionPromise = null;
      };
      db.onclose = () => {
        dbConnectionPromise = null;
      };
      resolve(db);
    };
    request.onerror = (event: Event) => {
      ensureErrorAndReject(reject)((event.target as IDBOpenDBRequest).error);
    };
    request.onblocked = () => {
      ensureErrorAndReject(reject)(new Error('IndexedDB open request blocked by another open connection'));
    };
  });
};

const openSharedConnection = (stores: IndexedDBStore[] = INDEX_DB_STORES, version = DB_VERSION) => {
  dbConnectionPromise ??= openNewConnection(stores, version).catch(error => {
    dbConnectionPromise = null;
    throw error;
  });
  return dbConnectionPromise;
};

const initializeIDB = (stores: IndexedDBStore[] = INDEX_DB_STORES, version = DB_VERSION) => {
  return openSharedConnection(stores, version).then(db => {
    console.info('IndexDB successfully connected!');
    return db;
  });
};

const insertValues = (storeName: string, values: unknown[]) => {
  return new Promise((resolve, reject) => {
    openSharedConnection().then(db => {
      try {
        const writeTransaction = db.transaction(storeName, 'readwrite');
        const store = writeTransaction.objectStore(storeName);
        values.forEach(f => store.put(f));
        writeTransaction.oncomplete = () => resolve(values);
        writeTransaction.onerror = (event: Event) => {
          ensureErrorAndReject(reject)((event.target as IDBTransaction).error);
        };
        writeTransaction.onabort = (event: Event) => {
          ensureErrorAndReject(reject)((event.target as IDBTransaction).error ?? new Error('IndexedDB write transaction aborted'));
        };
      } catch (error) {
        ensureErrorAndReject(reject)(error);
      }
    }, ensureErrorAndReject(reject));
  });
};

const deleteValues = (storeName: string, keys: unknown[]) => {
  return new Promise((resolve, reject) => {
    openSharedConnection().then(db => {
      try {
        const writeTransaction = db.transaction(storeName, 'readwrite');
        const store = writeTransaction.objectStore(storeName);
        keys.forEach(key => store.delete(key as IDBValidKey));
        writeTransaction.oncomplete = () => resolve(keys);
        writeTransaction.onerror = (event: Event) => {
          ensureErrorAndReject(reject)((event.target as IDBTransaction).error);
        };
        writeTransaction.onabort = (event: Event) => {
          ensureErrorAndReject(reject)((event.target as IDBTransaction).error ?? new Error('IndexedDB write transaction aborted'));
        };
      } catch (error) {
        ensureErrorAndReject(reject)(error);
      }
    }, ensureErrorAndReject(reject));
  });
};

const queryIndex = (storeName: string, index: string, value: string) => {
  return new Promise((resolve, reject) => {
    openSharedConnection().then(db => {
      try {
        const queryTransaction = db.transaction(storeName);
        const store = queryTransaction.objectStore(storeName);
        const values = store.index(index).getAll(IDBKeyRange.only(value));
        values.onsuccess = () => resolve(values.result);
        values.onerror = (event: Event) => ensureErrorAndReject(reject)((event.target as IDBRequest).error);
        queryTransaction.onabort = (event: Event) => {
          ensureErrorAndReject(reject)((event.target as IDBTransaction).error ?? new Error('IndexedDB read transaction aborted'));
        };
      } catch (error) {
        ensureErrorAndReject(reject)(error);
      }
    }, ensureErrorAndReject(reject));
  });
};

export const IndexedDBPromiseAPI = {
  initializeIDB,
  insertValues,
  deleteValues,
  queryIndex
};

export const INDEX_DB_STORES = [
  {
    name: FIELDS_STORE_NAME,
    keyPath: 'id',
    indexes: [{ name: PROPERTY_ID_INDEX, unique: false }]
  },
  {
    name: FIELDS_BY_SEASONS_STORE_NAME,
    keyPath: ['seasonId', 'id'],
    indexes: [{ name: PROPERTY_ID_INDEX, unique: false }]
  },
  {
    name: FIELDS_BY_SEASONS_SYNC_META_STORE_NAME,
    keyPath: ['propertyId', 'seasonId'],
    indexes: [{ name: SYNC_META_PROPERTY_ID_INDEX, unique: false }]
  }
];
